"use client";

/**
 * LiquidPillExpansible — design language v2
 * ──────────────────────────────────────────────────────────────────────
 * Composite genérico para una LiquidPill que puede revelar contenido debajo
 * de su header. Mantiene todo el material/hover/reactive del componente
 * `LiquidPill` y evita que consumidores reimplementen manualmente una
 * superficie con `LiquidSurface`.
 *
 * Uso típico:
 *   - FAQ expandible
 *   - item de settings con detalle
 *   - chat/conversación que se abre dentro de la misma pill
 */

import * as React from "react";
import { cn } from "@/lib/utils";
import { LiquidPill, type LiquidPillSize } from "./liquid-pill";
import type {
  LiquidDeformAxis,
  LiquidSurfaceBounceIntensity,
  LiquidSurfaceShadow,
} from "./liquid-surface";
import type { CircleActionVariant } from "./circle-action-variants";

/**
 * Altura mínima del header (estado colapsado) por tamaño.
 * Nota: `xxs` mantiene 56px por compatibilidad con consumidores existentes
 * (chats individuales del ChatList) que esperan ese tamaño histórico para
 * centrar vertical correctamente sus avatares dentro de la cell wrapper.
 * Los tamaños superiores conservan su escala visual.
 */
const HEADER_MIN_HEIGHT_BY_SIZE: Record<LiquidPillSize, number> = {
  xxs: 56,
  xs: 40,
  sm: 48,
  md: 56,
  lg: 60,
  xl: 68,
  "2xl": 80,
  "card-cta": 44,
};

export interface LiquidPillExpansibleProps {
  variant?: CircleActionVariant;
  active?: boolean;
  disabled?: boolean;
  header: React.ReactNode;
  content?: React.ReactNode;
  onToggle?: React.MouseEventHandler<HTMLButtonElement>;
  onHeaderMouseEnter?: React.MouseEventHandler<HTMLButtonElement>;
  shape?: "pill" | "rounded";
  expandedShape?: "pill" | "rounded";
  size?: LiquidPillSize;
  shadow?: LiquidSurfaceShadow;
  bounceIntensity?: LiquidSurfaceBounceIntensity;
  deformAxis?: LiquidDeformAxis;
  contentMaxHeight?: number;
  headerClassName?: string;
  contentClassName?: string;
  className?: string;
  ariaLabel?: string;
}

export function LiquidPillExpansible({
  variant = "slate",
  active = false,
  disabled = false,
  header,
  content,
  onToggle,
  onHeaderMouseEnter,
  shape = "pill",
  expandedShape = "rounded",
  size = "xxs",
  shadow = "neutral",
  bounceIntensity = "strong",
  deformAxis = "both",
  contentMaxHeight = 620,
  headerClassName,
  contentClassName,
  className,
  ariaLabel,
}: LiquidPillExpansibleProps) {
  const hasContent = content != null && content !== false;
  const isExpanded = active && hasContent;

  return (
    <LiquidPill
      as="div"
      variant={variant}
      appearance={isExpanded ? "normal" : "tinted"}
      shape={isExpanded ? expandedShape : shape}
      size={size}
      surfaceStyle="default"
      shadow={shadow}
      interactive={!disabled && !isExpanded}
      disabled={disabled || isExpanded}
      reactive
      hoverBounce={!disabled && !isExpanded}
      hoverDeform={!disabled && !isExpanded}
      bounceIntensity={bounceIntensity}
      deformAxis={deformAxis}
      cursorGlow={!disabled && !isExpanded}
      className={cn(
        "liquid-pill-expansible",
        isExpanded && "liquid-pill-expansible--expanded",
        disabled && "liquid-pill-expansible--disabled",
        className,
      )}
      style={{
        ["--lpe-content-max" as any]: `${contentMaxHeight}px`,
        ["--lpe-header-min" as any]: `${HEADER_MIN_HEIGHT_BY_SIZE[size]}px`,
      } as React.CSSProperties}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={onToggle}
        onMouseEnter={onHeaderMouseEnter}
        aria-expanded={hasContent ? active : undefined}
        aria-label={ariaLabel}
        className={cn(
          "liquid-pill-expansible__header",
          headerClassName,
        )}
      >
        {header}
      </button>

      {isExpanded && (
        <div
          className={cn(
            "liquid-pill-expansible__content",
            contentClassName,
          )}
        >
          <div className="liquid-pill-expansible__content-inner">
            {content}
          </div>
        </div>
      )}

      <style jsx global>{`
        .liquid-pill-expansible {
          width: 100% !important;
          max-width: 100% !important;
          display: flex !important;
          flex-direction: column !important;
          align-items: stretch !important;
          justify-content: flex-start !important;
          gap: 0 !important;
          padding: 0 !important;
          overflow: hidden;
        }

        .liquid-pill-expansible__header {
          position: relative;
          z-index: 2;
          display: inline-flex;
          width: 100%;
          max-width: 100%;
          min-width: 0;
          min-height: var(--lpe-header-min, 56px);
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
          flex: 0 0 auto;
          padding: 0;
          border: 0;
          background: transparent;
          color: inherit;
          text-align: left;
          cursor: pointer;
          font: inherit;
          line-height: 1;
        }
        .liquid-pill-expansible:not(.liquid-pill-expansible--expanded) .liquid-pill-expansible__header {
          height: var(--lpe-header-min, 56px);
          min-height: var(--lpe-header-min, 56px);
          max-height: var(--lpe-header-min, 56px);
        }
        .liquid-pill-expansible__header:disabled {
          cursor: default;
          pointer-events: none;
        }
        .liquid-pill-expansible__header:focus-visible {
          outline: none;
        }

        /* ── Apertura líquida del contenido ───────────────────────
           Coreografía en paralelo a la del grupo (los hermanos están
           colapsando con stagger desde t=0). Arrancamos con un pequeño
           delay para que el contenido entre cuando los hermanos ya
           empezaron a esfumarse y la pill activa ya está cambiando de
           radius. La animación compone:
             · height 0 → var(--lpe-content-max) — la celda crece física-
               mente en sincronía con el reveal (sin saltos), y deja una
               altura definida para que los hijos puedan usar h-full.
             · opacity 0 → 1 hacia el 45% (fade in temprano).
             · translateY -6px → 0 con micro overshoot en scaleY.
             · blur 5px → 0 (tensión superficial liberándose).
           Es flex column para que el contenido interno pueda estirarse
           con flex: 1 y así widgets como ChatWindow (h-full) ocupen toda
           la celda. */
        .liquid-pill-expansible__content {
          position: relative;
          z-index: 2;
          width: 100%;
          height: var(--lpe-content-max, 620px);
          display: flex;
          flex-direction: column;
          transform-origin: center top;
          overflow: hidden;
          animation: liquid-pill-expansible-flow 540ms
            cubic-bezier(0.22, 1.02, 0.32, 1) 80ms both;
          will-change: height, transform, opacity, filter;
        }
        @keyframes liquid-pill-expansible-flow {
          0% {
            opacity: 0;
            height: 0;
            transform: translateY(-6px) scaleY(0.94);
            filter: blur(5px);
          }
          45% {
            opacity: 1;
            filter: blur(1.5px);
          }
          75% {
            transform: translateY(0.5px) scaleY(1.012);
            filter: blur(0);
          }
          100% {
            opacity: 1;
            height: var(--lpe-content-max, 620px);
            transform: translateY(0) scaleY(1);
            filter: blur(0);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .liquid-pill-expansible__content {
            animation: none;
          }
        }
        .liquid-pill-expansible__content-inner {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          flex: 1 1 auto;
          min-height: 0;
          overflow: hidden;
          padding-top: 6px;
          padding-bottom: 6px;
        }
      `}</style>
    </LiquidPill>
  );
}

export default LiquidPillExpansible;
